import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Eye, UploadCloud, X, History, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, mediaUrl, formatApiErrorDetail, formatDate } from "@/lib/api";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { adminBtnPrimary, adminBtnGhost, adminField, adminLabel, StatusBadge } from "@/components/admin/ui";

// UTC ISO -> "YYYY-MM-DDTHH:mm" in America/New_York for datetime-local input
function toETInput(iso) {
  if (!iso) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }).formatToParts(new Date(iso)).reduce((acc, p) => ((acc[p.type] = p.value), acc), {});
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  } catch { return ""; }
}

const EMPTY = {
  title: "", subtitle: "", slug: "", excerpt: "", content: "", featured_image: "", image_alt: "",
  author: "George Goldman", category: "", tags: [], status: "draft", publish_at: "",
  show_toc: false, featured: false, seo_title: "", seo_description: "",
};

export default function ArticleEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [loaded, setLoaded] = useState(isNew);
  const [savedId, setSavedId] = useState(isNew ? null : id);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [showRev, setShowRev] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const dirty = useRef(false);

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/articles/${id}`).then((r) => {
      const a = r.data;
      setForm({
        title: a.title || "", subtitle: a.subtitle || "", slug: a.slug || "", excerpt: a.excerpt || "",
        content: a.content || "", featured_image: a.featured_image || "", image_alt: a.image_alt || "",
        author: a.author || "George Goldman", category: a.category || "", tags: a.tags || [],
        status: a.status || "draft", publish_at: toETInput(a.publish_at), show_toc: !!a.show_toc,
        featured: !!a.featured, seo_title: a.seo_title || "", seo_description: a.seo_description || "",
      });
      setRevisions(a.revisions || []);
      setLoaded(true);
    }).catch(() => { toast.error("Could not load article"); navigate("/admin/articles"); });
  }, [id, isNew, navigate]);

  const set = (k, v) => { dirty.current = true; setForm((f) => ({ ...f, [k]: v })); };

  const persist = useCallback(async (overrides = {}, silent = false) => {
    const payload = { ...form, ...overrides };
    if (!payload.title.trim()) { if (!silent) toast.error("Please add a title before saving."); return null; }
    setSaving(true);
    try {
      let res;
      if (savedId) res = await api.put(`/admin/articles/${savedId}`, payload);
      else res = await api.post("/admin/articles", payload);
      setSavedId(res.data.id);
      setForm((f) => ({ ...f, slug: res.data.slug, status: res.data.status }));
      setLastSaved(new Date());
      dirty.current = false;
      if (!silent) toast.success("Saved");
      if (isNew && res.data.id) window.history.replaceState(null, "", `/admin/articles/${res.data.id}`);
      return res.data;
    } catch (e) {
      if (!silent) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
      return null;
    } finally {
      setSaving(false);
    }
  }, [form, savedId, isNew]);

  // Autosave every 25s if dirty and has title
  useEffect(() => {
    const t = setInterval(() => { if (dirty.current && form.title.trim()) persist({}, true); }, 25000);
    return () => clearInterval(t);
  }, [persist, form.title]);

  const uploadFeatured = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("featured_image", data.url);
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const doPublish = async () => {
    const r = await persist({ status: "published", publish_at: "" });
    if (r) toast.success("Article published");
  };
  const doSchedule = async () => {
    if (!form.publish_at) { toast.error("Choose a publish date and time (Eastern Time)."); return; }
    const r = await persist({ status: "scheduled" });
    if (r) toast.success("Article scheduled");
  };

  if (!loaded) return <p className="py-20 text-center font-sans text-steel-400">Loading…</p>;

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <button onClick={() => navigate("/admin/articles")} className="flex items-center gap-2 font-sans text-sm text-steel-600 hover:text-gold-600"><ArrowLeft className="h-4 w-4" /> All articles</button>
        <div className="flex flex-wrap items-center gap-3">
          <StatusBadge status={form.status} />
          {lastSaved && <span className="font-sans text-xs text-steel-400">Saved {lastSaved.toLocaleTimeString()}</span>}
          <button onClick={() => setPreview(true)} className={adminBtnGhost} data-testid="preview-button"><Eye className="h-4 w-4" /> Preview</button>
          <button onClick={() => persist()} disabled={saving} className={adminBtnGhost} data-testid="save-draft-button"><Save className="h-4 w-4" /> {saving ? "Saving…" : "Save"}</button>
          <button onClick={doPublish} disabled={saving} className={adminBtnPrimary} data-testid="publish-button"><Send className="h-4 w-4" /> Publish</button>
        </div>
      </div>

      <div className="grid gap-8 lg:grid-cols-3">
        {/* Main column */}
        <div className="space-y-5 lg:col-span-2">
          <input value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Article title" className="w-full border-b border-ink/15 bg-transparent pb-2 font-serif text-4xl font-light text-ink placeholder:text-steel-300 focus:outline-none" data-testid="article-title" />
          <input value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} placeholder="Subtitle (optional)" className="w-full bg-transparent font-serif text-xl font-light italic text-steel-600 placeholder:text-steel-300 focus:outline-none" data-testid="article-subtitle" />
          <RichTextEditor value={form.content} onChange={(html) => set("content", html)} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <div className="border border-ink/10 bg-cream-100 p-5">
            <label className={adminLabel}>Publishing</label>
            <div className="mt-3 space-y-3">
              <div className="flex gap-2">
                {["draft", "published", "scheduled"].map((st) => (
                  <button key={st} onClick={() => set("status", st)} className={`flex-1 rounded-sm border px-2 py-1.5 font-sans text-xs capitalize ${form.status === st ? "border-gold-500 bg-gold-500 text-cream-100" : "border-ink/15 text-ink/70"}`} data-testid={`status-${st}`}>{st}</button>
                ))}
              </div>
              {form.status === "scheduled" && (
                <div>
                  <label className="font-sans text-xs text-steel-500">Publish at (Eastern Time)</label>
                  <input type="datetime-local" value={form.publish_at} onChange={(e) => set("publish_at", e.target.value)} className={adminField} data-testid="publish-at" />
                  <button onClick={doSchedule} className={`mt-2 w-full ${adminBtnGhost}`} data-testid="schedule-button"><Clock className="h-4 w-4" /> Schedule</button>
                </div>
              )}
              <label className="flex items-center gap-2 font-sans text-sm text-ink"><input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="featured-checkbox" /> Feature this article</label>
              <label className="flex items-center gap-2 font-sans text-sm text-ink"><input type="checkbox" checked={form.show_toc} onChange={(e) => set("show_toc", e.target.checked)} data-testid="toc-checkbox" /> Show table of contents</label>
            </div>
          </div>

          <div className="border border-ink/10 bg-cream-100 p-5">
            <label className={adminLabel}>Featured image</label>
            {form.featured_image ? (
              <div className="relative mt-3">
                <img src={mediaUrl(form.featured_image)} alt="" className="aspect-video w-full object-cover" />
                <button onClick={() => set("featured_image", "")} className="absolute right-2 top-2 rounded-full bg-ink/70 p-1 text-cream-100"><X className="h-4 w-4" /></button>
              </div>
            ) : (
              <label className="mt-3 flex cursor-pointer flex-col items-center justify-center border border-dashed border-ink/20 py-8 text-steel-400 hover:border-gold-500" data-testid="featured-image-drop">
                <UploadCloud className="h-6 w-6" />
                <span className="mt-2 font-sans text-xs">Upload image</span>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadFeatured(e.target.files[0])} />
              </label>
            )}
            <input value={form.image_alt} onChange={(e) => set("image_alt", e.target.value)} placeholder="Image alt text" className={adminField} data-testid="image-alt" />
          </div>

          <div className="border border-ink/10 bg-cream-100 p-5 space-y-4">
            <div>
              <label className={adminLabel}>Category</label>
              <select value={form.category} onChange={(e) => set("category", e.target.value)} className={adminField} data-testid="article-category">
                <option value="">Uncategorized</option>
                {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className={adminLabel}>Author</label>
              <input value={form.author} onChange={(e) => set("author", e.target.value)} className={adminField} data-testid="article-author" />
            </div>
            <div>
              <label className={adminLabel}>Tags <span className="text-steel-400">(comma separated)</span></label>
              <input value={form.tags.join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} className={adminField} data-testid="article-tags" />
            </div>
            <div>
              <label className={adminLabel}>Excerpt</label>
              <textarea rows={3} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} className={adminField} data-testid="article-excerpt" />
            </div>
          </div>

          <div className="border border-ink/10 bg-cream-100 p-5 space-y-4">
            <label className={adminLabel}>URL &amp; SEO</label>
            <div>
              <label className="font-sans text-xs text-steel-500">Slug</label>
              <input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="url-slug" className={adminField} data-testid="article-slug" />
              <p className="mt-1 font-sans text-xs text-steel-400">Changing a published slug creates an automatic redirect.</p>
            </div>
            <div>
              <label className="font-sans text-xs text-steel-500">SEO title</label>
              <input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} className={adminField} data-testid="seo-title" />
            </div>
            <div>
              <label className="font-sans text-xs text-steel-500">SEO description</label>
              <textarea rows={2} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} className={adminField} data-testid="seo-description" />
            </div>
          </div>

          {revisions.length > 0 && (
            <div className="border border-ink/10 bg-cream-100 p-5">
              <button onClick={() => setShowRev((s) => !s)} className="flex w-full items-center justify-between font-sans text-sm font-medium text-ink"><span className="flex items-center gap-2"><History className="h-4 w-4" /> Revision history ({revisions.length})</span></button>
              {showRev && (
                <ul className="mt-3 space-y-2">
                  {[...revisions].reverse().map((r, i) => (
                    <li key={i} className="flex items-center justify-between font-sans text-xs text-steel-500">
                      <span>{formatDate(r.at)} {new Date(r.at).toLocaleTimeString()}</span>
                      <button onClick={() => { set("content", r.content); set("title", r.title); toast.success("Revision loaded into editor"); }} className="text-gold-600 gf-link">Restore</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Preview modal */}
      {preview && (
        <div className="fixed inset-0 z-[120] overflow-y-auto bg-ink/60 backdrop-blur-sm" onClick={() => setPreview(false)}>
          <div className="mx-auto my-10 max-w-3xl bg-cream-100 p-8 lg:p-12" onClick={(e) => e.stopPropagation()} data-testid="preview-modal">
            <div className="mb-6 flex items-center justify-between">
              <span className="font-sans text-xs uppercase tracking-[0.2em] text-gold-600">Draft Preview</span>
              <button onClick={() => setPreview(false)}><X className="h-5 w-5 text-steel-600" /></button>
            </div>
            {form.category && <p className="font-sans text-xs uppercase tracking-[0.2em] text-gold-600">{form.category}</p>}
            <h1 className="mt-2 font-serif text-4xl font-light text-ink">{form.title || "Untitled"}</h1>
            {form.subtitle && <p className="mt-3 font-serif text-xl font-light italic text-steel-600">{form.subtitle}</p>}
            {form.featured_image && <img src={mediaUrl(form.featured_image)} alt={form.image_alt} className="mt-6 w-full" />}
            <div className="gf-prose mt-8" dangerouslySetInnerHTML={{ __html: form.content }} />
          </div>
        </div>
      )}
    </>
  );
}
